import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const articleImageMap: Record<string, string> = {
  'diyibanzhu-zhize-quanmian-jiexi': 'https://readdy.ai/api/search-image?query=Professional%20minimalist%20office%20workspace%20with%20warm%20lighting%20and%20clean%20composition%2C%20modern%20tech%20aesthetic%2C%20soft%20cream%20and%20beige%20tones%2C%20abstract%20geometric%20elements%2C%20editorial%20style%20photography&width=1200&height=500&seq=article-hero-zhize-01&orientation=landscape',
  'diyibanzhu-quanxian-tixi-xiangjie': 'https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20shield%20icon%20concept%2C%20warm%20beige%20background%2C%20soft%20natural%20lighting%2C%20minimalist%20professional%20photography%2C%20abstract%20security%20theme&width=1200&height=500&seq=article-hero-quanxian-01&orientation=landscape',
  'diyibanzhu-renming-biaozhun-yu-liucheng': 'https://readdy.ai/api/search-image?query=Modern%20office%20with%20certificate%20and%20checklist%20concept%2C%20warm%20cream%20tones%2C%20professional%20atmosphere%2C%20clean%20minimalist%20composition%2C%20soft%20natural%20light%2C%20editorial%20style&width=1200&height=500&seq=article-hero-renming-01&orientation=landscape',
  'shequ-neirong-guanli-shiyong-jiqiao': 'https://readdy.ai/api/search-image?query=Organized%20desk%20with%20documents%20and%20laptop%2C%20warm%20beige%20background%2C%20modern%20minimalist%20workspace%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20clean%20composition&width=1200&height=500&seq=article-hero-neirong-01&orientation=landscape',
  'banzhu-tuandui-jianshe-yu-guanli': 'https://readdy.ai/api/search-image?query=Team%20collaboration%20abstract%20concept%2C%20warm%20cream%20tones%2C%20modern%20minimalist%20office%20atmosphere%2C%20soft%20natural%20lighting%2C%20professional%20editorial%20photography%2C%20clean%20geometric%20composition&width=1200&height=500&seq=article-hero-team-01&orientation=landscape',
  'shequ-wenhua-jianshe-celve': 'https://readdy.ai/api/search-image?query=Community%20gathering%20abstract%20concept%20with%20connected%20circles%2C%20warm%20cream%20background%2C%20modern%20minimalist%20design%2C%20soft%20lighting%2C%20harmonious%20composition%2C%20editorial%20style&width=1200&height=500&seq=article-hero-wenhua-01&orientation=landscape',
  'yonghu-chengchang-tixi-sheji': 'https://readdy.ai/api/search-image?query=Growth%20chart%20abstract%20concept%2C%20warm%20golden%20tones%2C%20minimalist%20professional%20design%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20editorial%20style%20photography&width=1200&height=500&seq=article-hero-growth-01&orientation=landscape',
  'cong-putong-banzhu-dao-diyibanzhu': 'https://readdy.ai/api/search-image?query=Career%20growth%20staircase%20concept%2C%20warm%20golden%20tones%2C%20minimalist%20professional%20atmosphere%2C%20clean%20geometric%20design%2C%20soft%20natural%20lighting%2C%20editorial%20photography&width=1200&height=500&seq=article-hero-jinsheng-01&orientation=landscape',
  'chenggong-ban-kuai-anli-jiexi': 'https://readdy.ai/api/search-image?query=Success%20case%20study%20abstract%20concept%2C%20warm%20beige%20background%2C%20modern%20minimalist%20design%2C%20clean%20geometric%20shapes%2C%20professional%20editorial%20photography%2C%20soft%20natural%20light&width=1200&height=500&seq=article-hero-case-01&orientation=landscape',
  'weiji-chuli-yu-yuqing-guanli': 'https://readdy.ai/api/search-image?query=Crisis%20management%20abstract%20concept%2C%20warm%20cream%20tones%2C%20shield%20and%20alert%20theme%2C%20minimalist%20professional%20design%2C%20soft%20lighting%2C%20editorial%20photography&width=1200&height=500&seq=article-hero-weiji-01&orientation=landscape',
};

const articleJsonLd = (article: typeof articles[0]) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.excerpt,
  author: {
    '@type': 'Organization',
    name: article.author,
  },
  datePublished: article.date,
  url: `https://diyibanzhu.com/article/${article.slug}`,
  publisher: {
    '@type': 'Organization',
    name: '第一版主',
  },
  inLanguage: 'zh-CN',
});

const breadcrumbJsonLd = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-16">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '文章未找到' }]} />
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md text-sm font-medium transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const heroImage = articleImageMap[article.slug] || articleImageMap['diyibanzhu-zhize-quanmian-jiexi'];

  return (
    <>
      <SEOHead
        title={`${article.title} - 第一版主`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://diyibanzhu.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://diyibanzhu.com/article/${article.slug}`}
        jsonLdScripts={[
          articleJsonLd(article),
          breadcrumbJsonLd([
            { name: '首页', url: 'https://diyibanzhu.com/' },
            { name: category?.name || '分类', url: `https://diyibanzhu.com/category/${article.categorySlug}` },
            { name: article.title, url: `https://diyibanzhu.com/article/${article.slug}` },
          ]),
        ]}
      />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: category?.name || '分类', to: `/category/${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <article className="w-full py-8 md:py-12 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} 分钟阅读</span>
            </div>
          </header>

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
              相关文章
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {related.title}
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-foreground-400">
                    <span>{related.date}</span>
                    <span>{related.readTime} 分钟</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}